import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";

/**
 * Authentication State
 */
interface AuthState {
  isAuthenticated: boolean;
  preSignupToken: string | null;
  preMfaToken: string | null;
  user: User | null;
}

/**
 * User Information
 */
interface User {
  id: string;
  email: string;
  fullName: string;
  companyName: string;
  hasMfa: boolean;
}

/**
 * Auth Context Value
 */
interface AuthContextValue {
  // State
  isAuthenticated: boolean;
  user: User | null;
  preSignupToken: string | null;
  preMfaToken: string | null;

  // Actions
  setPreSignupToken: (token: string | null) => void;
  setPreMfaToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  clearTokens: () => void;
}

/**
 * Create Auth Context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

import { env, STORAGE_KEYS } from "@monorepo/config";
import { cookieManager } from "./cookie-manager";
import {
  setPreSignupToken as storageSetPreSignupToken,
  getPreSignupToken as storageGetPreSignupToken,
  clearPreSignupToken as storageClearPreSignupToken,
  setPreMfaToken as storageSetPreMfaToken,
  getPreMfaToken as storageGetPreMfaToken,
  clearPreMfaToken as storageClearPreMfaToken,
  clearAllTokens as storageClearAllTokens,
} from "./token-manager";

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 *
 * Manages authentication state in memory and syncs with tokenManager
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    preSignupToken: storageGetPreSignupToken(),
    preMfaToken: storageGetPreMfaToken(),
    user: null,
  });

  const [loading, setLoading] = useState(() => {
    // If we have an active user email cookie, we start in loading state to check the session
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      // Skip loading state during OIDC callback so we don't block rendering
      if (urlParams.get("code")) {
        return false;
      }
      return !!cookieManager.get(STORAGE_KEYS.USER_EMAIL);
    }
    return false;
  });

  /**
   * Check session on mount if user email cookie exists
   */
  useEffect(() => {
    const checkSession = async () => {
      if (typeof window === "undefined") return;

      const urlParams = new URLSearchParams(window.location.search);
      // Skip session check during OIDC callback to prevent clearing Authentik cookies
      // if the session isn't fully established yet.
      if (urlParams.get("code")) {
        return;
      }

      const userEmail = cookieManager.get(STORAGE_KEYS.USER_EMAIL);

      if (userEmail) {
        try {
          const response = await fetch(`${env.apiBaseUrl}/profile/get`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-userinfo": userEmail,
            },
            credentials: "include",
          });

          if (response.ok) {
            const data = await response.json();
            if (data.status === true && data.details) {
              const apiUser = data.details;
              setAuthState({
                isAuthenticated: true,
                preSignupToken: storageGetPreSignupToken(),
                preMfaToken: storageGetPreMfaToken(),
                user: {
                  id: apiUser.email_id,
                  email: apiUser.email_id,
                  fullName: apiUser.full_name || "",
                  companyName: apiUser.company_name || "",
                  hasMfa: apiUser.mfa_enabled === "true" || apiUser.mfa_enabled === true,
                },
              });
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error("Session verification failed:", error);
        }
      }

      // If we reach here, session is invalid or incomplete (e.g. missing userEmail), clear cookies
      const hostname = window.location.hostname;
      const hostnameParts = hostname.split(".");
      const cookieDomain = hostnameParts.length >= 2 ? `.${hostnameParts.slice(-2).join(".")}` : "";

      cookieManager.remove(STORAGE_KEYS.AUTHENTIK_SESSION, cookieDomain);
      cookieManager.remove(STORAGE_KEYS.AUTHENTIK_CSRF, cookieDomain);
      cookieManager.remove(STORAGE_KEYS.USER_EMAIL, cookieDomain);
      cookieManager.clearCurrent(STORAGE_KEYS.AUTHENTIK_SESSION);
      cookieManager.clearCurrent(STORAGE_KEYS.AUTHENTIK_CSRF);
      cookieManager.clearCurrent(STORAGE_KEYS.USER_EMAIL);

      setAuthState({
        isAuthenticated: false,
        preSignupToken: storageGetPreSignupToken(),
        preMfaToken: storageGetPreMfaToken(),
        user: null,
      });
      setLoading(false);
    };

    const urlParams = new URLSearchParams(window.location.search);
    const hasEmail = !!cookieManager.get(STORAGE_KEYS.USER_EMAIL);
    if (hasEmail && !urlParams.get("code")) {
      checkSession();
    } else {
      setLoading(false);
    }
  }, []);

  /**
   * Set pre-signup token (after email OTP verification)
   */
  const setPreSignupToken = useCallback((token: string | null) => {
    if (token) {
      storageSetPreSignupToken(token);
    } else {
      storageClearPreSignupToken();
    }
    setAuthState((prev) => ({
      ...prev,
      preSignupToken: token,
    }));
  }, []);

  /**
   * Set pre-MFA token (after password set, before MFA)
   */
  const setPreMfaToken = useCallback((token: string | null) => {
    if (token) {
      storageSetPreMfaToken(token);
    } else {
      storageClearPreMfaToken();
    }
    setAuthState((prev) => ({
      ...prev,
      preMfaToken: token,
    }));
  }, []);

  /**
   * Set user information
   */
  const setUser = useCallback((user: User | null) => {
    setAuthState((prev) => ({
      ...prev,
      user,
      isAuthenticated: user !== null,
    }));
  }, []);

  /**
   * Login (called after successful authentication)
   */
  const login = useCallback((user: User) => {
    storageClearAllTokens();
    setAuthState({
      isAuthenticated: true,
      user,
      preSignupToken: null,
      preMfaToken: null,
    });
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(() => {
    storageClearAllTokens();
    setAuthState({
      isAuthenticated: false,
      preSignupToken: null,
      preMfaToken: null,
      user: null,
    });
  }, []);

  /**
   * Clear all tokens
   */
  const clearTokens = useCallback(() => {
    storageClearAllTokens();
    setAuthState((prev) => ({
      ...prev,
      preSignupToken: null,
      preMfaToken: null,
    }));
  }, []);

  /**
   * Listen for unauthorized events from HTTP client
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);

    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, [logout]);

  const value: AuthContextValue = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    preSignupToken: authState.preSignupToken,
    preMfaToken: authState.preMfaToken,
    setPreSignupToken,
    setPreMfaToken,
    setUser,
    login,
    logout,
    clearTokens,
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        backgroundColor: "var(--bg-primary, #ffffff)",
        color: "var(--text-primary, #000000)",
        fontFamily: "system-ui, sans-serif"
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "4px solid var(--border-light, #e2e8f0)",
          borderTop: "4px solid var(--primary, #3b82f6)",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Access authentication state and actions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, logout } = useAuth();
 *
 *   return (
 *     <div>
 *       {isAuthenticated ? (
 *         <button onClick={logout}>Logout</button>
 *       ) : (
 *         <p>Please log in</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}

/**
 * RequireAuth Component
 * Redirect if not authenticated
 */
interface RequireAuthProps {
  children: ReactNode;
  redirectTo?: string;
}

export function RequireAuth({
  children,
  redirectTo = "/signup",
}: RequireAuthProps) {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = redirectTo;
    }
  }, [isAuthenticated, redirectTo]);

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

/**
 * RequireToken Component
 * Ensure pre-signup or pre-MFA token exists
 */
interface RequireTokenProps {
  children: ReactNode;
  tokenType: "preSignup" | "preMfa";
  redirectTo?: string;
}

export function RequireToken({
  children,
  tokenType,
  redirectTo = "/signup",
}: RequireTokenProps) {
  const { preSignupToken, preMfaToken } = useAuth();

  const hasToken = tokenType === "preSignup" ? preSignupToken : preMfaToken;

  useEffect(() => {
    if (!hasToken) {
      window.location.href = redirectTo;
    }
  }, [hasToken, redirectTo]);

  if (!hasToken) {
    return null;
  }

  return <>{children}</>;
}
